<script setup lang="ts">
import type { BubbleEmits, BubbleProps } from './types';

const props = withDefaults(defineProps<BubbleProps>(), {
  content: '',
  avatar: '',
  avatarText: '',
  avatarIcon: '',
  placement: 'start',
  variant: 'filled',
  maxWidth: '500px',
  avatarSize: '',
  avatarGap: '12px',
  avatarShape: 'circle',
  avatarSrcSet: '',
  avatarAlt: '',
  avatarFit: 'cover',
  noStyle: false,
});

const emit = defineEmits<BubbleEmits>();

const internalDestroyed = ref(false);
const avatarLoadFailed = ref(false);
const showAvatar = computed(() => Boolean(props.avatarIcon || props.avatar || props.avatarText));
const avatarSrc = computed(() => props.avatarIcon || avatarLoadFailed.value ? undefined : props.avatar);
const showAvatarText = computed(() => !props.avatarIcon && (!props.avatar || avatarLoadFailed.value));

watch(() => props.content, (n, o) => {
  if (n !== o && internalDestroyed.value) {
    restart();
  }
});

watch(() => props.avatar, () => {
  avatarLoadFailed.value = false;
});

function avatarError(e: Event) {
  avatarLoadFailed.value = true;
  emit('avatarError', e);
}

function restart() {
  internalDestroyed.value = false;
}

function destroy() {
  internalDestroyed.value = true;
}

onUnmounted(destroy);

defineExpose({ restart, destroy });
</script>

<template>
  <div
    v-if="!internalDestroyed"
    class="elx-bubble" :class="[
      placement === 'start' ? 'elx-bubble--start' : '',
      placement === 'end' ? 'elx-bubble--end' : '',
      noStyle ? 'elx-bubble--no-style' : '',
    ]"
    :style="{
      '--el-box-shadow-tertiary': `0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)`,
      '--elx-bubble-content-max-width': maxWidth,
      '--el-bubble-avatar-placeholder-width': $slots.avatar ? '' : avatarSize,
      '--el-bubble-avatar-placeholder-height': $slots.avatar ? '' : avatarSize,
      '--el-bubble-avatar-placeholder-gap': avatarGap,
    }"
  >
    <!-- 头像（avatarIcon 优先于 avatar URL） -->
    <div
      v-if="!$slots.avatar && showAvatar"
      class="elx-bubble__avatar elx-bubble__avatar-size"
      :class="{ 'is-text-fallback': showAvatarText }"
    >
      <el-avatar
        :size="0"
        :src="avatarSrc"
        :shape="avatarShape"
        :src-set="avatarSrcSet"
        :alt="avatarAlt"
        @error="avatarError"
      >
        <template v-if="avatarIcon" #default>
          <el-icon><component :is="avatarIcon" /></el-icon>
        </template>
        <template v-else-if="showAvatarText" #default>
          {{ avatarText || '？' }}
        </template>
      </el-avatar>
    </div>

    <!-- 头像占位 -->
    <!--    <div v-if="!$slots.avatar && !avatarIcon && !avatar && avatarSize" class="elx-bubble__avatar-placeholder" /> -->

    <div v-if="$slots.avatar" class="elx-bubble__avatar">
      <slot name="avatar" />
    </div>

    <!-- 内容 -->
    <div class="elx-bubble__content-wrapper">
      <div v-if="$slots.header" class="elx-bubble__header">
        <slot name="header" />
      </div>

      <div
        class="elx-bubble__content" :class="[
          loading ? 'elx-bubble__content--loading' : '',
          shape === 'round' && !noStyle ? 'elx-bubble__content--round' : '',
          shape === 'corner' && !noStyle ? 'elx-bubble__content--corner' : '',
          variant === 'filled' && !noStyle ? 'elx-bubble__content--filled' : '',
          variant === 'borderless' && !noStyle ? 'elx-bubble__content--borderless' : '',
          variant === 'outlined' && !noStyle ? 'elx-bubble__content--outlined' : '',
          variant === 'shadow' && !noStyle ? 'elx-bubble__content--shadow' : '',
        ]"
      >
        <!-- 默认文本内容 -->
        <div v-if="!loading && !$slots.content && content" class="elx-bubble__text">
          {{ content }}
        </div>

        <!-- 自定义内容 -->
        <slot v-if="!internalDestroyed && $slots.content && !loading" name="content" />

        <!-- 加载中 -->
        <div v-if="loading && !$slots.loading" class="elx-bubble__loading-wrap">
          <div
            v-for="(_, idx) in 3"
            :key="idx"
            class="elx-bubble__dot"
            :style="{ animationDelay: `${idx * 0.2}s` }"
          />
        </div>

        <div v-if="loading && $slots.loading" class="elx-bubble__loading-wrap">
          <slot name="loading" />
        </div>
      </div>

      <div v-if="$slots.footer" class="elx-bubble__footer">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use './style.scss';
</style>
